import {useState, type FormEvent, type ReactNode} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {ReadOnlyNotice} from '@/components/read-only-notice'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import {HelpIcon} from '@/components/help-icon'
import {TrazabilidadSwitch, useTrazabilidad} from '@/components/perfil/provenance-note'
import {Textarea} from '@/components/ui/textarea'
import {Skeleton} from '@/components/ui/skeleton'
import {FieldError} from '@/components/field-error'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useAgregarCondicion,
  useAlcanceCondiciones,
  useCondicionesCargo,
  useConfirmarTanda,
  useConfirmarCondicion,
  useDescartarCondicion,
  usePedirCondicionAInci,
  type CondicionAlcance,
  type CondicionCargo,
  type CondicionCodigo,
} from '@/hooks/empresa/perfil_cargo/use-condiciones-cargo'
import {detalleDelError, translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {formatInstantDate} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

/** Verde solo para lo que una persona confirmó: una derivada es una propuesta de Kairos. */
const TONO: Record<string, 'green' | 'amber' | 'neutral'> = {
  confirmada: 'green',
  derivada: 'amber',
  por_confirmar: 'amber',
  descartada: 'neutral',
}

const NOMBRES_EN_EL_RESUMEN = 5

function ALaFila({
  businessId,
  condicion,
  sufijo,
  children,
}: {
  businessId: string
  condicion: CondicionCargo
  sufijo: string
  children: ReactNode
}) {
  return (
    <Link
      className="underline"
      data-testid={`origen-fila-${sufijo}`}
      to={`/businesses/${businessId}/sgsst/matriz-riesgos?fila=${condicion.riesgo_fila_id}`}>
      {children}
    </Link>
  )
}

/** «No la detectó» solo es verdad si Kairos pudo mirar CIIU y Matriz; si falta alguno,
 * la frase dice qué no se pudo mirar. */
function claveSinDetectar(sinActividad: boolean, sinMatriz: boolean): string {
  if (sinActividad && sinMatriz) return 'condicionCargo.origen.noPudeMirar'
  if (sinMatriz) return 'condicionCargo.origen.sinDetectarSinMatriz'
  if (sinActividad) return 'condicionCargo.origen.sinDetectarSinActividad'
  return 'condicionCargo.origen.sinDetectar'
}

function OrigenCondicion({
  businessId,
  condicion,
  sufijo,
  sinDetectar,
  nombre,
  desplegado,
}: {
  businessId: string
  condicion: CondicionCargo
  sufijo: string
  sinDetectar: string
  nombre: string
  desplegado: boolean
}) {
  const {t} = useTranslation()
  const deLaMatriz = condicion.origen_linea === 'riesgo_fila' && condicion.riesgo_fila_id && condicion.numero_fila
  if (!desplegado) return null
  return (
    <PorQue
      label={t('common.deDondeSalio')}
      titulo={t('common.deDondeSalio')}
      ariaLabel={`${t('common.deDondeSalio')} — ${nombre}`}
      contenidoTestId={`origen-condicion-contenido-${sufijo}`}
      siempreVisible>
      <p>
        {condicion.origen_fila_perdida ? (
          t('condicionCargo.origen.filaPerdida')
        ) : deLaMatriz ? (
          <ALaFila businessId={businessId} condicion={condicion} sufijo={sufijo}>
            {condicion.nivel_riesgo
              ? t('condicionCargo.origen.riesgo_fila', {fila: condicion.numero_fila, nivel: condicion.nivel_riesgo})
              : t('condicionCargo.origen.riesgo_fila_sin_nivel', {fila: condicion.numero_fila})}
          </ALaFila>
        ) : (
          t(condicion.origen_linea ? `condicionCargo.origen.${condicion.origen_linea}` : sinDetectar)
        )}
        {condicion.del_area && <> · {t('condicionCargo.origen.delArea')}</>}
      </p>
      {condicion.confirmado_en && (
        <p data-testid={`confirmada-${sufijo}`}>
          {t('condicionCargo.confirmada', {
            nombre: condicion.confirmado_por_nombre ?? '',
            fecha: formatInstantDate(condicion.confirmado_en),
            personas: condicion.alcance_personas ?? 0,
          })}
        </p>
      )}
      {condicion.fuerza === 'descartada' && condicion.descartado_en && (
        <p data-testid={`descartada-${sufijo}`}>
          {t(condicion.descartado_motivo ? 'condicionCargo.descartada' : 'condicionCargo.descartadaSinMotivo', {
            nombre: condicion.descartado_por_nombre ?? '',
            fecha: formatInstantDate(condicion.descartado_en),
            motivo: condicion.descartado_motivo ?? '',
          })}
        </p>
      )}
    </PorQue>
  )
}

/** El estado de norma va por requisito (S-52). Sobre una descartada no se imprime:
 * contradiría el descarte. */
function RequisitosDeLaCondicion({condicion, sufijo}: {condicion: CondicionCargo; sufijo: string}) {
  const {t} = useTranslation()
  if (condicion.requisitos.length === 0 || condicion.fuerza === 'descartada') return null
  return (
    <div className="text-xs text-muted-foreground" data-testid={`requisitos-${sufijo}`}>
      <p>
        {t(
          condicion.fuerza === 'confirmada' ? 'condicionCargo.requisitosNacidos' : 'condicionCargo.requisitosSiConfirma'
        )}
      </p>
      <ul className="list-disc pl-5">
        {condicion.requisitos.map((requisito) => (
          <li key={requisito.nombre}>
            {requisito.nombre}
            {requisito.norma_estado !== 'verificada' && (
              <> · {t(`condicionCargo.normaEstado.${requisito.norma_estado}`)}</>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** E10-H03, D5. Se muestran las diecisiete aunque no se detecten: ocultarlas diría «no le
 * corresponde». Descartar no pide motivo (KAI-160). */
export function PerfilCondiciones({
  businessId,
  positionId,
  puedeFirmar,
  bloqueoFirma,
}: {
  businessId: string
  positionId: string
  puedeFirmar: boolean
  bloqueoFirma: string | null
}) {
  const {t} = useTranslation()
  // Cinturón de modo dios propio: el renglón libre y el pedido a Inci escriben en la empresa y
  // `puedeFirmar` no los cubre; el `editable` de la página exige además perfil sin confirmar.
  const godReadOnly = useGodModeReadOnly()
  const puedeCapturar = !godReadOnly
  const condiciones = useCondicionesCargo(businessId, positionId)
  const agregar = useAgregarCondicion(businessId, positionId)
  const pedirAlcance = useAlcanceCondiciones(businessId, positionId)
  const confirmarTanda = useConfirmarTanda(businessId, positionId)
  const confirmarUna = useConfirmarCondicion(businessId, positionId)
  const descartar = useDescartarCondicion(businessId, positionId)
  const pedirAInci = usePedirCondicionAInci(businessId, positionId)
  const [marcadas, setMarcadas] = useState<CondicionCodigo[]>([])
  const [descartadas, setDescartadas] = useState<CondicionCodigo[]>([])
  // Por id: un cargo puede tener varios renglones libres con el mismo código `libre`.
  const [marcadosLibres, setMarcadosLibres] = useState<string[]>([])
  const [alcance, setAlcance] = useState<CondicionAlcance | null>(null)
  const [aMotivar, setAMotivar] = useState<CondicionCargo | null>(null)
  const [motivo, setMotivo] = useState('')
  const [errorMotivo, setErrorMotivo] = useState<string | undefined>()
  const [texto, setTexto] = useState('')
  const [errorTexto, setErrorTexto] = useState<string | undefined>()
  const [aviso, setAviso] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [trazabilidad, setTrazabilidad] = useTrazabilidad()

  if (condiciones.isPending) return <Skeleton className="h-64 w-full" data-testid="condiciones-cargando" />
  if (condiciones.isError) {
    const cargoMuerto = detalleDelError(condiciones.error) === 'Position not found'
    return (
      <div
        className="notice-amber max-w-2xl space-y-2 rounded-lg px-3 py-2 text-sm"
        data-testid="condiciones-error-carga">
        <p>{t(cargoMuerto ? 'condicionCargo.errors.cargoNoEncontrado' : 'condicionCargo.errors.cargaFallida')}</p>
        {cargoMuerto ? (
          <Link className="underline" to={`/businesses/${businessId}/sgsst/perfiles-cargo`}>
            {t('condicionCargo.errors.volverALosCargos')}
          </Link>
        ) : (
          <Button size="sm" variant="outline" onClick={() => void condiciones.refetch()}>
            {t('perfilCargo.reintentar')}
          </Button>
        )}
      </div>
    )
  }
  const datos = condiciones.data
  const sinDetectar = claveSinDetectar(datos.sin_actividad_economica, datos.sin_matriz)
  // Los libres van siempre después de las diecisiete; su ordinal da testids únicos
  // (strict mode de Playwright).
  const libresDesde = datos.condiciones.findIndex((condicion) => condicion.codigo === 'libre')
  const derivadas = datos.condiciones.filter(
    (condicion) => condicion.fuerza === 'derivada' && condicion.codigo !== 'libre'
  )

  function sufijoDe(condicion: CondicionCargo, indice: number) {
    return condicion.codigo === 'libre' ? `libre-${indice - libresDesde}` : condicion.codigo
  }

  function alternar(condicion: CondicionCargo) {
    setDescartadas((previas) => previas.filter((otra) => otra !== condicion.codigo))
    if (condicion.codigo === 'libre' && condicion.id) {
      const id = condicion.id
      setMarcadosLibres((previos) => (previos.includes(id) ? previos.filter((otro) => otro !== id) : [...previos, id]))
      return
    }
    setMarcadas((previas) =>
      previas.includes(condicion.codigo)
        ? previas.filter((otra) => otra !== condicion.codigo)
        : [...previas, condicion.codigo]
    )
  }

  function alternarDescarte(condicion: CondicionCargo) {
    setMarcadas((previas) => previas.filter((otra) => otra !== condicion.codigo))
    setDescartadas((previas) =>
      previas.includes(condicion.codigo)
        ? previas.filter((otra) => otra !== condicion.codigo)
        : [...previas, condicion.codigo]
    )
  }

  function marcada(condicion: CondicionCargo) {
    return condicion.codigo === 'libre' && condicion.id
      ? marcadosLibres.includes(condicion.id)
      : marcadas.includes(condicion.codigo)
  }

  function marcadaParaDescarte(condicion: CondicionCargo) {
    return condicion.codigo !== 'libre' && descartadas.includes(condicion.codigo)
  }

  /** Confirmar sin previsualización; el servidor lo vuelve a evaluar antes de escribir. */
  function trivial(condicion: CondicionCargo) {
    return !!condicion.id && condicion.requisitos.length === 0 && datos.total_personas <= 1
  }

  function abrirTanda() {
    setError(null)
    setAviso(null)
    pedirAlcance.mutate(
      {confirmar: marcadas, confirmarIds: marcadosLibres, descartar: descartadas},
      {onSuccess: setAlcance, onError: (err) => setError(translateApiError(err))}
    )
  }

  function firmarTanda() {
    if (!alcance) return
    confirmarTanda.mutate(
      {
        condicionIds: alcance.confirmar.map((fila) => fila.id),
        descartes: alcance.descartar.map((fila) => ({condicion_id: fila.id})),
        alcanceToken: alcance.alcance_token,
      },
      {
        onSuccess: (data) => {
          setAlcance(null)
          setMarcadas([])
          setMarcadosLibres([])
          setDescartadas([])
          setAviso(t('condicionCargo.tanda.listo', {confirmadas: data.confirmadas, descartadas: data.descartadas}))
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  function confirmarTrivial(condicion: CondicionCargo) {
    setError(null)
    setAviso(null)
    confirmarUna.mutate(condicion.id!, {
      onSuccess: () => setAviso(t('condicionCargo.confirmadaSola')),
      onError: (err) => setError(translateApiError(err)),
    })
  }

  /** Una condición solo propuesta se materializa antes: el descarte necesita una fila. */
  async function descartarYa(condicion: CondicionCargo) {
    setError(null)
    setAviso(null)
    try {
      const condicionId = condicion.id ?? (await agregar.mutateAsync({codigo: condicion.codigo})).id
      await descartar.mutateAsync({condicionId: condicionId!})
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  function abrirMotivo(condicion: CondicionCargo) {
    setMotivo(condicion.descartado_motivo ?? '')
    setErrorMotivo(undefined)
    setAMotivar(condicion)
  }

  function cerrarMotivo() {
    setAMotivar(null)
    setMotivo('')
    setErrorMotivo(undefined)
  }

  /** Sobre una descartada solo escribe el motivo, sin volver a sellar quién y cuándo. */
  async function guardarMotivo(event: FormEvent) {
    event.preventDefault()
    if (!aMotivar?.id) return
    try {
      await descartar.mutateAsync({condicionId: aMotivar.id, motivo: motivo.trim()})
      cerrarMotivo()
    } catch (err) {
      setErrorMotivo(translateApiError(err))
    }
  }

  function agregarLibre(event: FormEvent) {
    event.preventDefault()
    const hallazgo = validateRequired(texto)
    setErrorTexto(hallazgo ? t(hallazgo) : undefined)
    if (hallazgo) return
    // Se vacía al éxito, no al enviar: un 422 o la falta de red no borran lo tecleado.
    agregar.mutate(
      {codigo: 'libre', textoLibre: texto.trim()},
      {onSuccess: () => setTexto(''), onError: (err) => setErrorTexto(translateApiError(err))}
    )
  }

  return (
    <section className="surface space-y-4 rounded-lg p-4" data-testid="perfil-condiciones">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 font-heading text-base tracking-tight">
          {t('condicionCargo.titulo')}
          {datos.catalogo_semilla && (
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('condicionCargo.titulo')})}
              testId="condiciones-semilla">
              {t('condicionCargo.catalogoSemilla')}
            </HelpIcon>
          )}
        </h2>
        <TrazabilidadSwitch desplegado={trazabilidad} onChange={setTrazabilidad} testId="condiciones-trazabilidad" />
      </div>

      {godReadOnly && (
        <ReadOnlyNotice testId="condiciones-readonly-banner">{t('condicionCargo.soloLectura')}</ReadOnlyNotice>
      )}

      {datos.sin_actividad_economica && (
        <section
          className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm"
          data-testid="condiciones-sin-actividad">
          <p>{t('condicionCargo.sinActividad')}</p>
          <Link className="underline" to={`/businesses/${businessId}/profile`}>
            {t('condicionCargo.sinActividadEnlace')}
          </Link>
        </section>
      )}
      {datos.sin_matriz && (
        <section className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="condiciones-sin-matriz">
          <p>{t('condicionCargo.sinMatriz')}</p>
          <Link className="underline" to={`/businesses/${businessId}/sgsst/matriz-riesgos`}>
            {t('condicionCargo.sinMatrizEnlace')}
          </Link>
        </section>
      )}
      {datos.total_personas === 0 && (
        <div
          className="notice-amber max-w-2xl space-y-1 rounded-lg px-3 py-2 text-sm"
          data-testid="condiciones-sin-personas">
          <p>{t('condicionCargo.sinPersonas')}</p>
          <PorQue label={t('common.porQue')} testId="condiciones-sin-personas-porque">
            <p>{t('condicionCargo.sinPersonasPorque')}</p>
          </PorQue>
        </div>
      )}

      {aviso && (
        <p className="notice-green max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="condiciones-aviso">
          {aviso}
        </p>
      )}
      {error && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="condiciones-error">
          {error}
        </p>
      )}

      {/* No se premarcan: nada se marca sin un gesto humano. */}
      {puedeFirmar && derivadas.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            data-testid="condiciones-marcar-derivadas"
            onClick={() => {
              setDescartadas([])
              setMarcadas(derivadas.map((condicion) => condicion.codigo))
            }}>
            {t('condicionCargo.marcarDerivadas', {count: derivadas.length})}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid="condiciones-quitar-marcas"
            onClick={() => {
              setMarcadas([])
              setMarcadosLibres([])
              setDescartadas([])
            }}>
            {t('condicionCargo.quitarTodas')}
          </Button>
        </div>
      )}

      <ul className="space-y-3">
        {datos.condiciones.map((condicion, indice) => {
          const sufijo = sufijoDe(condicion, indice)
          return (
            <li
              key={condicion.id ?? `${condicion.codigo}-${indice}`}
              className="space-y-1"
              data-testid={`condicion-${sufijo}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span className="text-sm text-foreground">
                  {condicion.texto_libre ?? t(`condicionCargo.codigo.${condicion.codigo}`)}
                </span>
                <Tag tone={condicion.fuerza ? TONO[condicion.fuerza] : 'neutral'}>
                  {t(`condicionCargo.fuerza.${condicion.fuerza ?? 'sinDetectar'}`)}
                </Tag>
              </div>
              <OrigenCondicion
                businessId={businessId}
                condicion={condicion}
                sufijo={sufijo}
                sinDetectar={sinDetectar}
                nombre={condicion.texto_libre ?? t(`condicionCargo.codigo.${condicion.codigo}`)}
                desplegado={trazabilidad}
              />
              <RequisitosDeLaCondicion condicion={condicion} sufijo={sufijo} />
              {condicion.sin_requisito_en_catalogo && (
                <p className="text-xs text-muted-foreground" data-testid={`sin-requisito-${sufijo}`}>
                  {condicion.codigo === 'libre'
                    ? t('condicionCargo.sinRequisitoLibre')
                    : t('condicionCargo.sinRequisito')}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {puedeFirmar && condicion.fuerza === 'descartada' && condicion.id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`motivo-${sufijo}`}
                    onClick={() => abrirMotivo(condicion)}>
                    {t('condicionCargo.agregarMotivo')}
                  </Button>
                )}
                {condicion.fuerza === 'confirmada' ? (
                  <>
                    {puedeFirmar && condicion.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`descartar-${sufijo}`}
                        disabled={descartar.isPending}
                        onClick={() => void descartarYa(condicion)}>
                        {t('condicionCargo.descartar')}
                      </Button>
                    )}
                    {puedeCapturar && condicion.sin_requisito_en_catalogo && condicion.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`pedir-a-inci-${sufijo}`}
                        disabled={pedirAInci.isPending}
                        onClick={() =>
                          pedirAInci.mutate(condicion.id!, {
                            onSuccess: () => setAviso(t('condicionCargo.pedida')),
                            onError: (err) => setError(translateApiError(err)),
                          })
                        }>
                        {t('condicionCargo.pedirAInci')}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {puedeFirmar && trivial(condicion) && (
                      <Button
                        size="sm"
                        data-testid={`confirmar-sola-${sufijo}`}
                        disabled={confirmarUna.isPending}
                        onClick={() => confirmarTrivial(condicion)}>
                        {t('condicionCargo.confirmarSola')}
                      </Button>
                    )}
                    {puedeFirmar && !trivial(condicion) && (
                      <Button
                        size="sm"
                        variant={marcada(condicion) ? 'default' : 'outline'}
                        data-testid={`marcar-${sufijo}`}
                        onClick={() => alternar(condicion)}>
                        {marcada(condicion) ? t('condicionCargo.desmarcar') : t('condicionCargo.marcar')}
                      </Button>
                    )}
                    {puedeFirmar && condicion.fuerza !== 'descartada' && condicion.codigo !== 'libre' && (
                      <Button
                        size="sm"
                        variant={marcadaParaDescarte(condicion) ? 'default' : 'ghost'}
                        data-testid={`descartar-${sufijo}`}
                        onClick={() => alternarDescarte(condicion)}>
                        {marcadaParaDescarte(condicion)
                          ? t('condicionCargo.quitarDescarte')
                          : t('condicionCargo.descartar')}
                      </Button>
                    )}
                    {puedeFirmar && condicion.fuerza !== 'descartada' && condicion.codigo === 'libre' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`descartar-${sufijo}`}
                        disabled={descartar.isPending || agregar.isPending}
                        onClick={() => void descartarYa(condicion)}>
                        {t('condicionCargo.descartar')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      <div className="space-y-1">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">{t('condicionCargo.universales')}</h3>
        <ul className="list-disc pl-5 text-sm text-muted-foreground" data-testid="condiciones-universales">
          {datos.universales.map((universal) => (
            <li key={universal}>{t(`condicionCargo.universal.${universal}`)}</li>
          ))}
        </ul>
      </div>

      {puedeFirmar && (
        <Button
          data-testid="condiciones-abrir-tanda"
          disabled={
            (marcadas.length === 0 && marcadosLibres.length === 0 && descartadas.length === 0) || pedirAlcance.isPending
          }
          onClick={abrirTanda}>
          {t('condicionCargo.tanda.abrir')}
        </Button>
      )}
      {!puedeFirmar && bloqueoFirma && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="condiciones-bloqueo-firma">
          {bloqueoFirma}
        </p>
      )}

      {puedeCapturar && (
        <form className="max-w-md space-y-2" noValidate onSubmit={agregarLibre}>
          <Label htmlFor="condicion-libre">{t('condicionCargo.textoLibre')}</Label>
          <Input
            maxLength={tope('NOTA')}
            id="condicion-libre"
            data-testid="condicion-libre"
            value={texto}
            onBlur={(event) => {
              const hallazgo = validateRequired(event.target.value)
              setErrorTexto(hallazgo ? t(hallazgo) : undefined)
            }}
            onChange={(event) => {
              setTexto(event.target.value)
              setErrorTexto(undefined)
            }}
          />
          <FieldError error={errorTexto} />
          <Button type="submit" size="sm" disabled={agregar.isPending}>
            {t('condicionCargo.agregarLibre')}
          </Button>
        </form>
      )}

      <p className="text-xs text-muted-foreground" data-testid="condiciones-versiones">
        {t('condicionCargo.versiones', {
          catalogo: datos.catalogo_version,
          tipificacion: datos.tipificacion_version,
        })}
      </p>

      <Dialog open={alcance !== null} onOpenChange={(abierto) => !abierto && setAlcance(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('condicionCargo.tanda.titulo')}</DialogTitle>
            <DialogDescription>
              {alcance && alcance.total_personas > 0
                ? t('condicionCargo.personas', {count: alcance.total_personas})
                : t('condicionCargo.sinPersonas')}
            </DialogDescription>
          </DialogHeader>
          {alcance && (
            <div className="space-y-2 text-sm text-muted-foreground">
              {alcance.personas.length > 0 && (
                <p data-testid="tanda-titulares">
                  {alcance.personas
                    .slice(0, NOMBRES_EN_EL_RESUMEN)
                    .map((persona) => persona.nombre)
                    .join(', ')}
                  {alcance.personas.length > NOMBRES_EN_EL_RESUMEN &&
                    ` ${t('condicionCargo.tanda.yMas', {count: alcance.personas.length - NOMBRES_EN_EL_RESUMEN})}`}
                </p>
              )}
              <p data-testid="tanda-resumen">
                {t('condicionCargo.tanda.resumen', {
                  confirmar: alcance.confirmar.length,
                  descartar: alcance.descartar.length,
                })}
              </p>
              <p data-testid="tanda-requisitos">
                {t('condicionCargo.tanda.requisitosNacen', {requisitos: alcance.requisitos_nacen})}
              </p>
              {alcance.personas_con_rojo === 0 && <p>{t('condicionCargo.tanda.sinRojos')}</p>}
              {alcance.trivial && <p data-testid="tanda-trivial">{t('condicionCargo.trivial')}</p>}
            </div>
          )}
          <div className="flex gap-2">
            <Button data-testid="tanda-confirmar" disabled={confirmarTanda.isPending} onClick={firmarTanda}>
              {t('condicionCargo.tanda.confirmar')}
            </Button>
            <Button type="button" variant="outline" onClick={() => setAlcance(null)}>
              {t('condicionCargo.tanda.cancelar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={aMotivar !== null} onOpenChange={(abierto) => !abierto && cerrarMotivo()}>
        {aMotivar && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {aMotivar.codigo === 'libre' ? aMotivar.texto_libre : t(`condicionCargo.codigo.${aMotivar.codigo}`)}
              </DialogTitle>
            </DialogHeader>
            <form className="space-y-3" noValidate onSubmit={(event) => void guardarMotivo(event)}>
              <Label htmlFor="condicion-motivo">{t('condicionCargo.motivo')}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="condicion-motivo"
                data-testid="condicion-motivo"
                value={motivo}
                onChange={(event) => {
                  setMotivo(event.target.value)
                  setErrorMotivo(undefined)
                }}
              />
              <FieldError error={errorMotivo} />
              <Button type="submit" size="sm" data-testid="condicion-motivo-guardar" disabled={descartar.isPending}>
                {t('common.save')}
              </Button>
            </form>
          </DialogContent>
        )}
      </Dialog>
    </section>
  )
}
